import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Server, HardDrive, Activity, RefreshCw, CheckCircle, AlertCircle, X, Trash2, Wifi, WifiOff, Loader2, Edit2 } from 'lucide-react';
import { useAppContext, ServerConnection, ServerType } from '../../context/AppContext';
const panelTypes: {
  value: ServerType;
  label: string;
  description: string;
  defaultPort: number;
  color: string;
}[] = [{
  value: 'cpanel',
  label: 'cPanel / WHM',
  description: 'Industry-standard web hosting control panel',
  defaultPort: 2087,
  color: 'bg-orange-500/10 text-orange-500'
}, {
  value: 'plesk',
  label: 'Plesk',
  description: 'Web hosting platform with WordPress toolkit',
  defaultPort: 8443,
  color: 'bg-blue-500/10 text-blue-500'
}, {
  value: 'sonicpanel',
  label: 'SonicPanel',
  description: 'Streaming media & radio server management',
  defaultPort: 2086,
  color: 'bg-cyan-500/10 text-cyan-500'
}, {
  value: 'directadmin',
  label: 'DirectAdmin',
  description: 'Lightweight, fast web hosting panel',
  defaultPort: 2222,
  color: 'bg-indigo-500/10 text-indigo-500'
}, {
  value: 'cyberpanel',
  label: 'CyberPanel',
  description: 'OpenLiteSpeed-based hosting panel',
  defaultPort: 8090,
  color: 'bg-green-500/10 text-green-500'
}, {
  value: 'cloudpanel',
  label: 'CloudPanel',
  description: 'Free server management for PHP apps',
  defaultPort: 8443,
  color: 'bg-sky-500/10 text-sky-500'
}, {
  value: 'virtualmin',
  label: 'Virtualmin',
  description: 'Powerful open-source hosting control panel',
  defaultPort: 10000,
  color: 'bg-violet-500/10 text-violet-500'
}, {
  value: 'ispconfig',
  label: 'ISPConfig',
  description: 'Open-source multi-server management',
  defaultPort: 8080,
  color: 'bg-amber-500/10 text-amber-500'
}, {
  value: 'centova',
  label: 'Centova Cast',
  description: 'Shoutcast & Icecast stream management',
  defaultPort: 2199,
  color: 'bg-pink-500/10 text-pink-500'
}, {
  value: 'whmcs',
  label: 'WHMCS',
  description: 'Billing & automation for hosting businesses',
  defaultPort: 443,
  color: 'bg-emerald-500/10 text-emerald-500'
}];
const getPanelInfo = (type: ServerType) => panelTypes.find(p => p.value === type) || panelTypes[0];
export function ServerIntegrations() {
  const {
    serverConnections,
    addServerConnection,
    updateServerConnection,
    deleteServerConnection
  } = useAppContext();
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<ServerConnection | null>(null);
  const [selectedType, setSelectedType] = useState<ServerType>('cpanel');
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const handleSync = (id: string) => {
    setIsSyncing(id);
    setTimeout(() => setIsSyncing(null), 1500);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to remove this server connection?')) {
      deleteServerConnection(id);
    }
  };
  const openAdd = () => {
    setEditingServer(null);
    setSelectedType('cpanel');
    setTestResult(null);
    setIsModalOpen(true);
  };
  const openEdit = (server: ServerConnection) => {
    setEditingServer(server);
    setSelectedType(server.type);
    setTestResult(null);
    setIsModalOpen(true);
  };
  const handleTestConnection = () => {
    setIsTesting(true);
    setTestResult(null);
    setTimeout(() => {
      setIsTesting(false);
      setTestResult(Math.random() > 0.2 ? 'success' : 'error');
    }, 2000);
  };
  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    const data: ServerConnection = {
      id: editingServer ? editingServer.id : `srv_${Date.now()}`,
      name: form.get('name') as string,
      type: selectedType,
      hostname: form.get('hostname') as string,
      port: parseInt(form.get('port') as string),
      apiKey: form.get('apiKey') as string,
      username: form.get('username') as string,
      status: testResult === 'success' ? 'connected' : editingServer ? editingServer.status : 'disconnected',
      lastSync: editingServer ? editingServer.lastSync : new Date().toISOString(),
      notes: form.get('notes') as string,
      stats: editingServer?.stats
    };
    if (editingServer) {
      updateServerConnection(data.id, data);
    } else {
      addServerConnection(data);
    }
    setIsModalOpen(false);
    setEditingServer(null);
  };
  return <div className="space-y-8" data-id="element-1026">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1027">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-1028">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1029">
            Server Integrations
          </h1>
          <p className="text-slate-400" data-id="element-1030">
            Connect and manage servers across all major control panels.
          </p>
        </motion.div>
        <button onClick={openAdd} className="bg-fox-600 hover:bg-fox-500 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors" data-id="element-1031">
          <Plus size={18} data-id="element-1032" /> Add Server
        </button>
      </div>

      {/* Supported Panels Info */}
      <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5" data-id="element-1033">
        <h3 className="text-sm font-bold text-white mb-3" data-id="element-1034">
          Supported Control Panels
        </h3>
        <div className="flex flex-wrap gap-2" data-id="element-1035">
          {panelTypes.map(p => <span key={p.value} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-800 ${p.color}`} data-id="element-1036">
              <Server size={12} data-id="element-1037" />
              {p.label}
            </span>)}
        </div>
      </div>

      {/* Server Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1038">
        {serverConnections.map((server, i) => {
        const panel = getPanelInfo(server.type);
        return <motion.div key={server.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1039">
              <div className="flex justify-between items-start mb-6" data-id="element-1040">
                <div className="flex items-center gap-3" data-id="element-1041">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${panel.color}`} data-id="element-1042">
                    <Server size={24} data-id="element-1043" />
                  </div>
                  <div data-id="element-1044">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2" data-id="element-1045">
                      {server.name}
                      {server.status === 'connected' ? <CheckCircle size={14} className="text-emerald-500" data-id="element-1046" /> : server.status === 'error' ? <AlertCircle size={14} className="text-red-500" data-id="element-1047" /> : <WifiOff size={14} className="text-slate-500" data-id="element-1048" />}
                    </h3>
                    <p className="text-slate-400 text-sm font-mono" data-id="element-1049">
                      {server.hostname}:{server.port}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-1050">
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wider border border-slate-800 ${panel.color}`} data-id="element-1051">
                    {panel.label}
                  </span>
                </div>
              </div>

              {server.notes && <p className="text-slate-500 text-sm mb-4 italic" data-id="element-1052">
                  "{server.notes}"
                </p>}

              {server.stats && <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-900 rounded-xl border border-slate-800" data-id="element-1053">
                  <div data-id="element-1054">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1" data-id="element-1055">
                      <Activity size={12} data-id="element-1056" /> Accounts
                    </p>
                    <p className="text-white font-bold" data-id="element-1057">
                      {server.stats.accounts}
                    </p>
                  </div>
                  <div data-id="element-1058">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1" data-id="element-1059">
                      <HardDrive size={12} data-id="element-1060" /> Disk
                    </p>
                    <p className="text-white font-bold" data-id="element-1061">
                      {server.stats.diskUsage}
                    </p>
                  </div>
                  <div data-id="element-1062">
                    <p className="text-slate-500 text-xs mb-1 flex items-center gap-1" data-id="element-1063">
                      <Activity size={12} data-id="element-1064" /> Bandwidth
                    </p>
                    <p className="text-white font-bold" data-id="element-1065">
                      {server.stats.bandwidth}
                    </p>
                  </div>
                </div>}

              <div className="flex items-center justify-between pt-4 border-t border-slate-800" data-id="element-1066">
                <p className="text-xs text-slate-500" data-id="element-1067">
                  Last sync: {new Date(server.lastSync).toLocaleString()}
                </p>
                <div className="flex items-center gap-3" data-id="element-1068">
                  <button onClick={() => openEdit(server)} className="text-sm text-slate-400 hover:text-white transition-colors" data-id="element-1069">
                    <Edit2 size={14} data-id="element-1070" />
                  </button>
                  <button onClick={() => handleDelete(server.id)} className="text-sm text-slate-400 hover:text-red-400 transition-colors" data-id="element-1071">
                    <Trash2 size={14} data-id="element-1072" />
                  </button>
                  <button onClick={() => handleSync(server.id)} disabled={isSyncing === server.id} className="flex items-center gap-2 text-sm text-fox-500 hover:text-fox-400 font-medium transition-colors disabled:opacity-50" data-id="element-1073">
                    <RefreshCw size={14} className={isSyncing === server.id ? 'animate-spin' : ''} data-id="element-1074" />
                    {isSyncing === server.id ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              </div>
            </motion.div>;
      })}

        {serverConnections.length === 0 && <div className="lg:col-span-2 text-center py-16 bg-slate-950 border border-slate-800 rounded-2xl" data-id="element-1075">
            <Server size={48} className="mx-auto text-slate-700 mb-4" data-id="element-1076" />
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-1077">
              No servers connected
            </h3>
            <p className="text-slate-500 mb-6" data-id="element-1078">
              Add your first server to start managing your infrastructure.
            </p>
            <button onClick={openAdd} className="bg-fox-600 hover:bg-fox-500 text-white px-5 py-2 rounded-lg font-medium transition-colors" data-id="element-1079">
              Add Server
            </button>
          </div>}
      </div>

      {/* Add/Edit Server Modal */}
      <AnimatePresence data-id="element-1080">
        {isModalOpen && <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-1081">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-id="element-1082">
              <div className="p-6 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-slate-900 z-10" data-id="element-1083">
                <h2 className="text-xl font-bold text-white" data-id="element-1084">
                  {editingServer ? 'Edit Server Connection' : 'Add New Server'}
                </h2>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-white" data-id="element-1085">
                  <X size={24} data-id="element-1086" />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 space-y-6" data-id="element-1087">
                {/* Panel Type Selection */}
                <div className="space-y-3" data-id="element-1088">
                  <label className="text-sm font-medium text-slate-300" data-id="element-1089">
                    Control Panel Type *
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-1" data-id="element-1090">
                    {panelTypes.map(panel => <button key={panel.value} type="button" onClick={() => setSelectedType(panel.value)} className={`p-3 rounded-xl border text-left transition-all ${selectedType === panel.value ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 bg-slate-950 hover:border-slate-700'}`} data-id="element-1091">
                        <div className="flex items-center gap-2 mb-1" data-id="element-1092">
                          <div className={`w-6 h-6 rounded flex items-center justify-center ${panel.color}`} data-id="element-1093">
                            <Server size={12} data-id="element-1094" />
                          </div>
                          <span className="text-white font-medium text-sm" data-id="element-1095">
                            {panel.label}
                          </span>
                        </div>
                        <p className="text-slate-500 text-[11px] leading-tight" data-id="element-1096">
                          {panel.description}
                        </p>
                      </button>)}
                  </div>
                </div>

                {/* Connection Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1097">
                  <div className="space-y-2 md:col-span-2" data-id="element-1098">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1099">
                      Server Name *
                    </label>
                    <input name="name" required defaultValue={editingServer?.name} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="e.g. US-East Production Server" data-id="element-1100" />
                  </div>

                  <div className="space-y-2" data-id="element-1101">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1102">
                      Hostname / IP *
                    </label>
                    <input name="hostname" required defaultValue={editingServer?.hostname} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="server.example.com" data-id="element-1103" />
                  </div>

                  <div className="space-y-2" data-id="element-1104">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1105">
                      Port *
                    </label>
                    <input name="port" type="number" required defaultValue={editingServer?.port || getPanelInfo(selectedType).defaultPort} key={selectedType} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" data-id="element-1106" />
                  </div>

                  <div className="space-y-2" data-id="element-1107">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1108">
                      Username
                    </label>
                    <input name="username" defaultValue={editingServer?.username} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="root / admin" data-id="element-1109" />
                  </div>

                  <div className="space-y-2" data-id="element-1110">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1111">
                      API Key / Token *
                    </label>
                    <input name="apiKey" required type="password" defaultValue={editingServer?.apiKey} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="••••••••••••" data-id="element-1112" />
                  </div>

                  <div className="space-y-2 md:col-span-2" data-id="element-1113">
                    <label className="text-sm font-medium text-slate-300" data-id="element-1114">
                      Notes
                    </label>
                    <input name="notes" defaultValue={editingServer?.notes} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 text-white focus:border-fox-500 focus:outline-none" placeholder="Optional description for this server" data-id="element-1115" />
                  </div>
                </div>

                {/* Test Connection */}
                <div className="bg-slate-950 border border-slate-800 rounded-xl p-4" data-id="element-1116">
                  <div className="flex items-center justify-between" data-id="element-1117">
                    <div data-id="element-1118">
                      <h4 className="text-white font-medium text-sm" data-id="element-1119">
                        Test Connection
                      </h4>
                      <p className="text-slate-500 text-xs" data-id="element-1120">
                        Verify credentials before saving
                      </p>
                    </div>
                    <button type="button" onClick={handleTestConnection} disabled={isTesting} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1121">
                      {isTesting ? <Loader2 size={14} className="animate-spin" data-id="element-1122" /> : <Wifi size={14} data-id="element-1123" />}
                      {isTesting ? 'Testing...' : 'Test Connection'}
                    </button>
                  </div>
                  {testResult && <motion.div initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`mt-3 p-3 rounded-lg flex items-center gap-2 text-sm ${testResult === 'success' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`} data-id="element-1124">
                      {testResult === 'success' ? <CheckCircle size={16} data-id="element-1125" /> : <AlertCircle size={16} data-id="element-1126" />}
                      {testResult === 'success' ? 'Connection successful! Server is reachable.' : 'Connection failed. Please check your credentials and try again.'}
                    </motion.div>}
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-slate-800 flex justify-end gap-3" data-id="element-1127">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors" data-id="element-1128">
                    Cancel
                  </button>
                  <button type="submit" className="bg-fox-600 hover:bg-fox-500 text-white px-6 py-2 rounded-lg font-medium transition-colors" data-id="element-1129">
                    {editingServer ? 'Save Changes' : 'Add Server'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}